import React, { useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { Input, MenuItem, Select } from '@material-ui/core';

import HealtheraTable from '../Table/Table';
import { TabButtons, TabHealthera } from '../../healtheraComponents';
import Switch from '../Shared/Switch';
import Tip from './Tip';
import Spinner from '../Shared/Spinner';
import TableContainer from '../Shared/TableContainer';

import useLocationQuery from '../../hooks/useQuery';
import { getProblematicPharmacies } from '../../redux/pharmacies';

import { formatDateTimeHumanFriendly } from '../../utils/formatters';
import { cutLongText } from '../../helpers';
import { DEFAULT_PHARMACY_LIST_LIMIT, ORDER_TYPE_UI } from './constants';
import { LIMIT_OPTIONS, ORDER_TYPES, PHARMACY_TYPES, TABS } from './constants';
import { getLimit, getPharmacyType } from './helpers';

const ProblematicPharmaciesList = () => {
  const dispatch = useDispatch();
  const { changeLocationQuery, query } = useLocationQuery();

  const { problematicPharmacies, totalPharmacies, isLoading } = useSelector(
    state => state.pharmacies
  );

  const preparedData = prepareData(problematicPharmacies);

  useEffect(() => {
    const { tab, page, pharmacy_type, limit, order_type } = query;

    dispatch(
      getProblematicPharmacies({
        pharmacyType: (pharmacy_type || PHARMACY_TYPES.OTC),
        status: (tab || TABS.ALL),
        orderType: order_type,
        page: (+page || 0),
        limit: (+limit || DEFAULT_PHARMACY_LIST_LIMIT)
      })
    );
  }, [dispatch, query]);

  const handleChangePage = (_, pageNumber) => {
    changeLocationQuery({
      ...query,
      page: pageNumber
    });
  };

  const handleChangePharmacyType = ({ target: { value: pharmacyType } }) => {
    changeLocationQuery({
      ...query,
      pharmacy_type: pharmacyType,
      page: 0
    });
  };

  const handleChangeTab = (e, tab) => {
    changeLocationQuery({
      ...query,
      tab,
      page: 0
    });
  };

  const handleReferredSwitch = (_, checked) => {
    const { order_type, ...queryParams } = query;

    changeLocationQuery({
      ...queryParams,
      ...(checked && { order_type: ORDER_TYPES.REFERRED })
    });
  };

  const handleRowsPerPageChange = useCallback(
    ({ target: { value } }) => {
      changeLocationQuery({
        ...query,
        limit: value,
        page: 0
      });
    },
    [changeLocationQuery, query]
  );

  return (
    <TableContainer>
      <TipContainer>
        <Tip>Only last month orders are counted</Tip>
        <Tip>You can press mouse wheel to open the orders list page in a new tab</Tip>
        <Tip>Page reload doesn't reset current table page and tab</Tip>
      </TipContainer>
      <NavBar>
        <TabButtons
          height="64px"
          onChange={handleChangeTab}
          value={Object.values(TABS).includes(query.tab) ? query.tab : TABS.ALL}
        >
          <TabHealthera value={TABS.ALL} label="All" />
          <TabHealthera value={TABS.REQUESTED} label="New" />
          <TabHealthera value={TABS.PROCESSING} label="Processing" />
          <TabHealthera value={TABS.READY} label="Ready" />
        </TabButtons>
        <SelectWrapper>
          <Select
            style={styles.select}
            input={<Input disableUnderline />}
            value={getPharmacyType(query.pharmacy_type)}
            onChange={handleChangePharmacyType}
            fullWidth
          >
            <MenuItem value={PHARMACY_TYPES.OTC}>OTC pharmacies</MenuItem>
            <MenuItem value={PHARMACY_TYPES.PDP}>Paid Delivery pharmacies</MenuItem>
            <MenuItem value={PHARMACY_TYPES.ODPP}>All referral pharmacies</MenuItem>
          </Select>
        </SelectWrapper>
        <ReferredSwitch
          label="Referred Orders Only"
          checked={query.order_type === ORDER_TYPES.REFERRED}
          value={query.order_type}
          onChange={handleReferredSwitch}
        />
      </NavBar>
      {isLoading ? (
        <Spinner />
      ) : (
        <TableWrapper>
          <HealtheraTable
            muted={true}
            data={preparedData}
            onChangePage={handleChangePage}
            displayPagination
            totalCount={totalPharmacies}
            page={+query.page || 0}
            rowsPerPage={getLimit(+query.limit)}
            rowsPerPageOptions={LIMIT_OPTIONS}
            onRowsPerPageChange={handleRowsPerPageChange}
          />
        </TableWrapper>
      )}
    </TableContainer>
  );
};

const prepareData = pharmacies => {
  if (!pharmacies) {
    return [];
  }

  return pharmacies.reduce(
    (data, pharmacy) => {
      const { pharmacy_id, pharmacy_name, postcode, last_note, orders_stats } = pharmacy;
      const { text, date_created } = last_note || {};

      data.push({
        view: {
          pharmacyName: pharmacy_name || '',
          postcode: postcode,
          priorityStatus: (
            <StatusWrapper>
              {Object.entries(orders_stats).map(([statsCategory, count]) => {
                const { color, status } = ORDER_TYPE_UI[statsCategory];
                return (
                  <PriorityStatus color={color}>
                    {status} ({count})
                  </PriorityStatus>
                );
              })}
            </StatusWrapper>
          ),
          lastNote: last_note && (
            <Note>
              {/* Support line breaks by rendering a new <p> tag for each line */}
              {cutLongText(text, 50)
                .split('\n')
                .map((textPart) => (
                  <NoteText key={textPart}>{textPart}</NoteText>
                ))}
              <NoteDate>
                {formatDateTimeHumanFriendly(date_created * 1000)}
              </NoteDate>
            </Note>
          ),
        },
        ID: pharmacy_id,
        route: 'problematic-pharmacies',
      });

      return data;
    },
    [
      {
        view: {
          pharmacyName: 'Pharmacy Name',
          postcode: 'Post Code',
          priorityStatus: 'Priority Status',
          lastNote: 'Last Note'
        },
        headers: true
      }
    ]
  );
};

const ReferredSwitch = styled(Switch)`
  margin-left: 10px;
`;

const TableWrapper = styled.div`
  padding: 0 10px;
`;

const NavBar = styled.nav`
  padding: 16px;
  display: flex;
  align-items: center;
`;

const SelectWrapper = styled.div`
  margin-left: 50px;
  padding-right: 40px;
  width: 350px;
`;

const StatusWrapper = styled.div`
  padding: 5px 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 5px;
`;

const TipContainer = styled.div`
  margin-top: 20px;
  margin-left: 20px;
`;

const Note = styled.div``;

const NoteText = styled.p`
  margin: 0;
  margin-bottom: 5px;
  font-size: 14px;
`;

const NoteDate = styled.span`
  font-size: 12px;
`;

const PriorityStatus = styled.div`
  color: #fff;
  padding: 7px;
  border-radius: 5px;
  font-weight: bold;
  background-color: ${(props) => props.color};
  min-width: 100px;
  text-align: center;
`;

const styles = {
  select: {
    padding: '6px 16px',
    backgroundColor: '#f9f9f9'
  }
};

export default ProblematicPharmaciesList;
